import {
  MRT_ColumnDef,
  Checkbox,
  ControlledCheckbox,
  ControlledSelect,
  MuiAutocomplete as Autocomplete,
  TextField,
  Tooltip,
  Typography,
  InfoIcon,
  Stack,
} from '@cherre-frontend/ui';
import React, { useMemo } from 'react';
import {
  type Control,
  type UseFormSetValue,
  type UseFormGetValues,
  Controller,
} from 'react-hook-form';
import {
  MappingFieldsTableValues,
  TargetTableFormValues,
  TSA_VALUES,
  TsaFieldsTableValues,
  TableMode,
} from '../schema';
import { MappingTableAutocompleteField } from '../components/MappingTableAutocompleteField';
import MappingLookupTableAutocomplete from '../components/MappingLookupTableAutocomplete';
import MappingSetsAutocomplete from '../components/MappingLookupColumnAutocomplete';

type UseMappingColumnDefinitionsProps = {
  tableMode?: TableMode;
  setMappingValues: UseFormSetValue<MappingFieldsTableValues>;
  getMappingValues: UseFormGetValues<MappingFieldsTableValues>;
  fieldNameOptions: Array<{ label: string; value: string }>;
};

type UseTsaColumnDefinitionsProps = {
  control: Control<TsaFieldsTableValues>;
  addTableRow: () => void;
  tableMode?: TableMode;
  fieldNameOptions: Array<{ label: string; value: string }>;
};

type UseSchemaColumnDefinitionsProps = {
  control: Control<TargetTableFormValues>;
  onFieldNameBlur?: () => void;
  tableMode?: TableMode;
  dsp?: boolean;
};

type DisplayPreference = NonNullable<
  MappingFieldsTableValues['fields'][number]['displayPreference']
>;
type DisplayPreferenceOption = { label: string; value: DisplayPreference };

const displayPreferenceOptions: DisplayPreferenceOption[] = [
  { label: 'Don’t Display', value: 'do_not_display' },
  { label: 'Submitted', value: 'submitted' },
  { label: 'Mapped', value: 'mapped' },
];

interface ColumnHeaderWithTooltipProps {
  header: string;
  tooltip: string;
}

const ColumnHeaderWithTooltip = (props: ColumnHeaderWithTooltipProps) => {
  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      gap={2}
    >
      <Typography variant='h6' fontSize='12px'>
        {props.header}
      </Typography>
      <Tooltip arrow placement='top' title={props.tooltip}>
        <InfoIcon />
      </Tooltip>
    </Stack>
  );
};

export const useMappingColumnDefinitions = ({
  tableMode,
  fieldNameOptions,
  getMappingValues,
  setMappingValues,
}: UseMappingColumnDefinitionsProps): Array<
  MRT_ColumnDef<MappingFieldsTableValues['fields'][number]>
> => {
  return useMemo(() => {
    return [
      {
        accessorKey: 'fieldName',
        header: 'Source Field',
        size: 150,
        Header: ({ column }) => (
          <ColumnHeaderWithTooltip
            header={column.columnDef.header}
            tooltip='The field from the Table Schema (e.g. gl_account_code)'
          />
        ),
        Cell: ({ row }) => (
          <MappingTableAutocompleteField
            getMappingValues={getMappingValues}
            setMappingValues={setMappingValues}
            label='Select field name'
            name={`fields.${row.index}.fieldName`}
            options={fieldNameOptions}
            disabled={tableMode !== TableMode.Create}
          />
        ),
      },
      {
        accessorKey: 'descriptionName',
        header: 'Source Description Field (Optional)',
        size: 150,
        Header: ({ column }) => (
          <ColumnHeaderWithTooltip
            header={column.columnDef.header}
            tooltip='Does the field identified under Source Field have a corresponding description field under the Table Schema? If it does, select the corresponding field (e.g. gl_account_description)'
          />
        ),
        Cell: ({ row }) => (
          <MappingTableAutocompleteField
            getMappingValues={getMappingValues}
            setMappingValues={setMappingValues}
            label='Select Description Field Name'
            name={`fields.${row.index}.descriptionName`}
            options={fieldNameOptions}
            disabled={tableMode !== TableMode.Create}
          />
        ),
      },
      {
        accessorKey: 'mappingName',
        header: 'Lookup Table',
        size: 150,
        Header: ({ column }) => (
          <ColumnHeaderWithTooltip
            header={column.columnDef.header}
            tooltip='What type of mapping is this? (e.g. GL Account Mapping)'
          />
        ),
        Cell: ({ row }) => (
          <MappingLookupTableAutocomplete
            getMappingValues={getMappingValues}
            setMappingValues={setMappingValues}
            fieldName={`fields.${row.index}.mappingName`}
            disabled={tableMode !== TableMode.Create}
          />
        ),
      },
      {
        accessorKey: 'mappingValue',
        header: 'Lookup Field',
        size: 150,
        Header: ({ column }) => (
          <ColumnHeaderWithTooltip
            header={column.columnDef.header}
            tooltip='Which column in the selected mapping table should be retrieved for the Output Field (e.g. “Target Code”)?'
          />
        ),
        Cell: ({ row }) => (
          <MappingSetsAutocomplete
            getMappingValues={getMappingValues}
            setMappingValues={setMappingValues}
            fieldName={`fields.${row.index}.mappingValue`}
            disabled={tableMode !== TableMode.Create}
            mappingFieldName={getMappingValues(
              `fields.${row.index}.mappingName`
            )}
          />
        ),
      },
      {
        accessorKey: 'targetName',
        header: 'Output Field',
        size: 150,
        Header: ({ column }) => (
          <ColumnHeaderWithTooltip
            header={column.columnDef.header}
            tooltip='The name of the output field to be added to the table schema (e.g. target_gl_account_code)'
          />
        ),
        Cell: ({ row }) => {
          const name = `fields.${row.index}.targetName` as const;
          return (
            <TextField
              fullWidth
              name={name}
              color='primary'
              variant='standard'
              label='Enter the Reference Column Name'
              value={getMappingValues(name)}
              disabled={tableMode !== TableMode.Create}
              onChange={({ target }) => setMappingValues(name, target.value)}
            />
          );
        },
      },
      {
        accessorKey: 'displayName',
        header: 'Output Field Display Name',
        size: 120,
        Header: ({ column }) => (
          <ColumnHeaderWithTooltip
            header={column.columnDef.header}
            tooltip='The display name corresponding to the Output Field (e.g. Target GL Account Code)'
          />
        ),
        Cell: ({ row }) => {
          const name = `fields.${row.index}.displayName` as const;
          return (
            <TextField
              fullWidth
              name={name}
              color='primary'
              variant='standard'
              label='Enter the Label Name'
              value={getMappingValues(name)}
              disabled={tableMode !== TableMode.Create}
              onChange={({ target }) => setMappingValues(name, target.value)}
            />
          );
        },
      },
      {
        accessorKey: 'displayPreference',
        header: 'Display Preference',
        size: 120,
        Header: ({ column }) => (
          <ColumnHeaderWithTooltip
            header={column.columnDef.header}
            tooltip='Should the Output Field be included in any corresponding package report and if so, should it only render when the Report View is set to Submitted or Mapped? (defaults to Don’t Display)'
          />
        ),
        Cell: ({ row }) => (
          <MappingTableAutocompleteField
            getMappingValues={getMappingValues}
            setMappingValues={setMappingValues}
            label='Select Display Preference'
            name={`fields.${row.index}.displayPreference`}
            options={displayPreferenceOptions}
            disabled={tableMode !== TableMode.Create}
          />
        ),
      },
      {
        accessorKey: 'required',
        header: 'Required?',
        size: 100,
        muiTableHeadCellProps: { align: 'center' as const },
        muiTableBodyCellProps: { align: 'center' as const },
        Header: ({ column }) => (
          <ColumnHeaderWithTooltip
            header={column.columnDef.header}
            tooltip='Is the Reference Column field required?'
          />
        ),
        Cell: ({ row }) => {
          const name = `fields.${row.index}.required` as const;
          return (
            <Checkbox
              name={name}
              checked={getMappingValues(name)}
              disabled={tableMode !== TableMode.Create}
              onChange={(_, checked) => setMappingValues(name, checked)}
            />
          );
        },
      },
    ];
  }, [fieldNameOptions, tableMode, getMappingValues, setMappingValues]);
};

export const useTsaColumnDefinitions = ({
  control,
  tableMode,
  fieldNameOptions,
}: UseTsaColumnDefinitionsProps) => {
  const columns: Array<MRT_ColumnDef<TsaFieldsTableValues['fields'][number]>> =
    useMemo(() => {
      return [
        {
          accessorKey: 'fieldName',
          header: 'Field Name',
          size: 150,
          Cell: ({ row }) => (
            <Controller
              control={control}
              name={`fields.${row.index}.fieldName`}
              render={({ ...props }) => (
                <Autocomplete
                  options={fieldNameOptions.map((option) => option.value)}
                  disabled={tableMode !== TableMode.Create}
                  value={props.field.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select field name'
                      variant='standard'
                      fullWidth
                    />
                  )}
                  onChange={(e, data) => props.field.onChange(data)}
                  fullWidth
                  {...props}
                />
              )}
            />
          ),
        },
        {
          accessorKey: 'tsa',
          header: 'TSA',
          size: 150,
          Cell: ({ row }) => (
            <ControlledSelect
              control={control}
              name={`fields.${row.index}.tsa`}
              fullWidth
              disabled={tableMode !== TableMode.Create}
              color='primary'
              variant='standard'
              options={[
                { label: 'Enabled', value: TSA_VALUES.ENABLED },
                { label: 'Required', value: TSA_VALUES.REQUIRED },
              ]}
            />
          ),
        },
      ];
    }, [tableMode, fieldNameOptions, control]);

  return columns;
};
export const useSchemaColumnDefinitions = ({
  control,
  onFieldNameBlur,
  tableMode,
  dsp = false,
}: UseSchemaColumnDefinitionsProps) => {
  const autoCompleteOptions = useMemo(() => {
    return [
      { label: 'Boolean', value: 'boolean' },
      { label: 'Currency', value: 'currency' },
      { label: 'Date', value: 'date' },
      { label: 'DateTime', value: 'datetime' },
      { label: 'Integer', value: 'integer' },
      { label: 'Number', value: 'number' },
      { label: 'String', value: 'string' },
      { label: 'YearMonth', value: 'yearmonth' },
    ];
  }, []);

  const columns: Array<
    MRT_ColumnDef<TargetTableFormValues['tableData'][number]>
  > = useMemo(() => {
    return [
      {
        accessorKey: 'name',
        header: 'Target Field Name',
        size: 150,
        Cell: ({ row }) => (
          <Controller
            control={control}
            name={`tableData.${row.index}.name`}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                disabled={tableMode !== TableMode.Create}
                color='primary'
                label='Enter field name'
                variant='standard'
                onBlur={onFieldNameBlur}
              />
            )}
          />
        ),
      },
      {
        accessorKey: 'displayName',
        header: 'Display Name',
        size: 150,
        Cell: ({ row }) => (
          <Controller
            control={control}
            name={`tableData.${row.index}.displayName`}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                disabled={tableMode === TableMode.Read}
                color='primary'
                label='Enter display name'
                variant='standard'
              />
            )}
          />
        ),
      },
      {
        accessorKey: 'type',
        header: 'Data Type',
        size: 150,
        Cell: ({ row }) => (
          <Controller
            control={control}
            name={`tableData.${row.index}.type`}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth
                options={autoCompleteOptions}
                disabled={tableMode !== TableMode.Create}
                value={
                  autoCompleteOptions.find(
                    ({ value }) => value === field.value
                  ) || null
                }
                onChange={(e, data) => field.onChange(data ? data.value : '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant='standard'
                    label='Select data type'
                  />
                )}
              />
            )}
          />
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description (Optional)',
        size: 150,
        Cell: ({ row }) => (
          <Controller
            control={control}
            name={`tableData.${row.index}.description`}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                disabled={tableMode === TableMode.Read}
                color='primary'
                label='Enter description'
                variant='standard'
              />
            )}
          />
        ),
      },
      ...(dsp
        ? [
            {
              accessorKey: 'dsp.defaultValue',
              header: 'Default Value (Optional)',
              size: 150,
              Cell: ({ row }) => (
                <Controller
                  control={control}
                  name={`tableData.${row.index}.dsp.defaultValue`}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      disabled={tableMode === TableMode.Read}
                      color='primary'
                      label='Enter default value'
                      variant='standard'
                    />
                  )}
                />
              ),
            },
            {
              accessorKey: 'dsp.required',
              header: 'Required?',
              muiTableHeadCellProps: {
                align: 'center' as const,
              },
              muiTableBodyCellProps: {
                align: 'center' as const,
              },
              size: 80,
              Cell: ({ row }) => (
                <ControlledCheckbox
                  aria-label='Required'
                  control={control}
                  disabled={tableMode === TableMode.Read}
                  name={`tableData.${row.index}.dsp.required`}
                />
              ),
            },
          ]
        : []),
    ];
  }, [onFieldNameBlur, autoCompleteOptions, tableMode, control, dsp]);

  return columns;
};
